import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { CiEdit } from 'react-icons/ci';
import { fetchJewellersSidebarRequest } from '../../../redux/JewellerList/JewellerSlice';
import {
  getLuckyDrawOptimsRequest,
  saveLuckyDrawOptimRequest,
  updateLuckyDrawOptimStatusRequest,
} from '../../../redux/LuckyDrawOptim/luckyDrawOptimSlice';
import TableCompo from '../Table/TableCompo';
import { apiRequest } from '../../../redux/Reports/reportsSlice';
import useCsvConverter from '../../../hooks/convertToCSV';

const formatDate = (value) =>
  value ? moment(value).format('DD-MM-YYYY') : '-';

const triggerCsvDownload = (csvText, filename) => {
  const blob = new Blob([csvText], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

const LuckyDrawOptim = () => {
  const dispatch = useDispatch();
  const { convertToCsv } = useCsvConverter();
  const { jewellersSidebar = [] } = useSelector((state) => state.jeweller);
  const {
    luckyDrawData = [],
    total = 0,
    loading,
    saving,
  } = useSelector((state) => state.luckyDrawOptim || {});

  const [selectedJewellers, setSelectedJewellers] = useState([]);
  const [minBuyLimit, setMinBuyLimit] = useState('');
  const [transactionType, setTransactionType] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [editId, setEditId] = useState(null);
  const [optInStatus, setOptInStatus] = useState(true);
  const [bullions, setBullions] = useState('All');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyJeweller, setHistoryJeweller] = useState('');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const listParams = {
    page: page + 1,
    limit,
    ...(search ? { search } : {}),
  };

  useEffect(() => {
    dispatch(fetchJewellersSidebarRequest({ allActive: true }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getLuckyDrawOptimsRequest(listParams));
  }, [dispatch, page, limit, search]);

  const jewellerOptions = [...jewellersSidebar].sort((a, b) =>
    (a.jewellerName || '').localeCompare(b.jewellerName || ''),
  );

  const usedJewellerIdSet = new Set(
    luckyDrawData
      .map((row) =>
        typeof row.jewellerId === 'string' ? row.jewellerId : row.jewellerId?._id,
      )
      .filter(Boolean),
  );

  const handleSave = () => {
    if (!selectedJewellers.length) {
      toast.error('Please select jeweller');
      return;
    }
    if (minBuyLimit === '' || Number(minBuyLimit) < 0) {
      toast.error('Please enter minimum buy amount');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Please select start date and end date');
      return;
    }
    if (endDate < startDate) {
      toast.error('End date cannot be before start date');
      return;
    }

    const dates = {
      startDate: new Date(`${startDate}T00:00:00`).getTime(),
      endDate: new Date(`${endDate}T23:59:59`).getTime(),
      minBuyLimit: Number(minBuyLimit),
      transactionType,
    };

    if (editId) {
      dispatch(
        saveLuckyDrawOptimRequest({
          body: { id: editId, optInStatus, bullions, ...dates },
          listParams,
        }),
      );
    } else {
      dispatch(
        saveLuckyDrawOptimRequest({
          body: { jewellerId: selectedJewellers, ...dates },
          listParams: { ...listParams, page: 1 },
        }),
      );
      setPage(0);
    }

    resetForm();
  };

  const resetForm = () => {
    setEditId(null);
    setSelectedJewellers([]);
    setMinBuyLimit('');
    setTransactionType('All');
    setStartDate('');
    setEndDate('');
    setOptInStatus(true);
    setBullions('All');
  };

  const handleEdit = (row) => {
    setEditId(row._id);
    setSelectedJewellers([row.jewellerId]);
    setMinBuyLimit(row.minBuyLimit ?? '');
    setTransactionType(
      row.transactionType === 'Sip' ? 'Installment' : row.transactionType || 'All',
    );
    setStartDate(row.startDate ? moment(row.startDate).format('YYYY-MM-DD') : '');
    setEndDate(row.endDate ? moment(row.endDate).format('YYYY-MM-DD') : '');
    setOptInStatus(!!row.optInStatus);
    setBullions(row.bullions || 'All');
  };

  const handleDownloadCsv = () => {
    dispatch(
      apiRequest({
        method: 'GET',
        endpoint: '/api/v1/jeweller-lucky-draw/getJewellerLuckyDrawsCsv',
        query: {},
        callback: (response) => {
          if (response?.error) {
            toast.error(response.message || 'Failed to download CSV');
            return;
          }
          const rows = Array.isArray(response?.data)
            ? response.data
            : response?.data?.data || [];
          const csv = convertToCsv(rows);
          if (!csv) {
            toast.error('No records to download');
            return;
          }
          triggerCsvDownload(csv, 'jeweller-lucky-draws.csv');
        },
      }),
    );
  };

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case 'changePage':
        setPage(tableState.page);
        break;
      case 'changeRowsPerPage':
        setLimit(tableState.rowsPerPage);
        setPage(0);
        break;
      case 'search':
        setSearch(tableState.searchText || '');
        setPage(0);
        break;
      default:
        break;
    }
  };

  const handleStatusClick = (row) => {
    setSelectedRow(row);
    setOpenConfirm(true);
  };

  const handleConfirm = () => {
    if (selectedRow) {
      dispatch(
        updateLuckyDrawOptimStatusRequest({
          id: selectedRow._id,
          optInStatus: !selectedRow.optInStatus,
        }),
      );
    }
    setOpenConfirm(false);
    setSelectedRow(null);
  };

  const handleCancel = () => {
    setOpenConfirm(false);
    setSelectedRow(null);
  };

  const tableRows = luckyDrawData.map((row) => ({
    ...row,
    startDateText: formatDate(row.startDate),
    endDateText: formatDate(row.endDate),
    status: row.optInStatus ? 'Opt In' : 'Opt Out',
    createdAtText: row.createdAt
      ? moment(row.createdAt).format('DD-MM-YYYY hh:mm A')
      : '-',
  }));

  const columns = [
    { field: 'createdAtText', headerName: 'Created At', minWidth: 170 },
    { field: 'jewellerName', headerName: 'Jeweller Name', minWidth: 180 },
    { field: 'minBuyLimit', headerName: 'minBuyLimit', minWidth: 140 },
    { field: 'transactionType', headerName: 'Transaction Type', minWidth: 160 },
    { field: 'startDateText', headerName: 'Start Date', minWidth: 140 },
    { field: 'endDateText', headerName: 'End Date', minWidth: 140 },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      render: (status, row) => (
        <Button
          variant="contained"
          style={{
            backgroundColor: row.optInStatus ? 'green' : '#d32f2f',
            color: 'white',
            textTransform: 'none',
          }}
          onClick={() => handleStatusClick(row)}
        >
          {status}
        </Button>
      ),
    },
    {
      field: 'history',
      headerName: 'History',
      minWidth: 120,
      hideInExcel: true,
      sortable: false,
      render: (val, row) => (
        <button
          style={{
            backgroundColor: '#ffc107',
            color: '#000',
            border: 'none',
            padding: '5px 10px',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
          onClick={() => {
            setHistoryRows(row.activities || []);
            setHistoryJeweller(row.jewellerName || '');
            setHistoryOpen(true);
          }}
        >
          View
        </button>
      ),
    },
    { field: 'createdByName', headerName: 'Created By', minWidth: 160 },
    {
      field: 'edit',
      headerName: 'Edit',
      minWidth: 100,
      hideInExcel: true,
      sortable: false,
      render: (val, row) => (
        <CiEdit
          style={{ cursor: 'pointer', fontSize: '22px' }}
          onClick={() => handleEdit(row)}
        />
      ),
    },
  ];

  const optinForm = (
    <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            disabled={!!editId}
            options={[
              ...jewellerOptions.filter(
                (item) =>
                  !usedJewellerIdSet.has(item.id) ||
                  selectedJewellers.includes(item.id),
              ),
              ...selectedJewellers
                .filter((id) => !jewellerOptions.some((item) => item.id === id))
                .map((id) => {
                  const editRow = luckyDrawData.find(
                    (item) => item._id === editId,
                  );
                  return {
                    id,
                    jewellerName:
                      editRow?.jewellerId === id ? editRow.jewellerName : id,
                  };
                }),
            ]}
            getOptionLabel={(option) => option?.jewellerName || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedJewellers.map((id) => {
              const jeweller = jewellerOptions.find((item) => item.id === id);
              if (jeweller) return jeweller;
              const editRow = luckyDrawData.find((item) => item._id === editId);
              return {
                id,
                jewellerName:
                  editRow?.jewellerId === id ? editRow.jewellerName : id,
              };
            })}
            onChange={(_, newValue) =>
              setSelectedJewellers(newValue.map((item) => item.id))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Jeweller"
                placeholder="Search jeweller"
              />
            )}
            sx={{ minWidth: 280, flex: '1 1 280px' }}
          />
          <TextField
            size="small"
            type="number"
            label="Minimum Buy Amount"
            inputProps={{ min: 0 }}
            value={minBuyLimit}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || Number(value) >= 0) setMinBuyLimit(value);
            }}
            sx={{ minWidth: 220, flex: '1 1 220px' }}
          />
          <TextField
            select
            label="Transaction Type"
            size="small"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
            sx={{ minWidth: 220, flex: '1 1 220px' }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Buy">Buy</MenuItem>
            <MenuItem value="Installment">Installment</MenuItem>
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Start Date"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => {
              const value = e.target.value;
              setStartDate(value);
              if (endDate && value && endDate < value) setEndDate('');
            }}
            sx={{ minWidth: 200, flex: '1 1 200px' }}
          />
          <TextField
            size="small"
            type="date"
            label="End Date"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate || undefined }}
            disabled={!startDate}
            value={endDate}
            onChange={(e) => {
              const value = e.target.value;
              if (startDate && value && value < startDate) return;
              setEndDate(value);
            }}
            sx={{ minWidth: 200, flex: '1 1 200px' }}
          />
          {editId && (
            <TextField
              select
              label="Status"
              size="small"
              value={optInStatus}
              onChange={(e) => setOptInStatus(e.target.value)}
              sx={{ minWidth: 200, flex: '1 1 200px' }}
            >
              <MenuItem value={true}>Opt In</MenuItem>
              <MenuItem value={false}>Opt Out</MenuItem>
            </TextField>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="contained"
            sx={{ bgcolor: '#752ADA', px: 6, textTransform: 'none' }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : editId ? 'Update' : 'Save'}
          </Button>
          {editId && (
            <Button
              variant="outlined"
              sx={{ px: 4, textTransform: 'none' }}
              onClick={resetForm}
            >
              Cancel
            </Button>
          )}
        </Box>
    </>
  );

  return (
    <Box sx={{ width: '100%', pr: 2, boxSizing: 'border-box' }}>
      <Paper
        elevation={2}
        sx={{ p: 3, width: '100%', border: '1px solid #ccc', boxSizing: 'border-box' }}
      >
        <Typography fontWeight={700} fontSize={24} mb={3} fontFamily={'Aeonik'}>
          Lucky Draw Optin
        </Typography>
        {!editId && optinForm}
      </Paper>

      <Box
        sx={{
          mt: 3,
          pb: 3,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'auto',
        }}
      >
        <Typography fontWeight={700} fontSize={24} fontFamily={'Aeonik'}>
          Lucky Draw Optin list
        </Typography>
        <TableCompo
          title="Lucky Draw Optim list"
          columns={columns}
          data={tableRows}
          totalCount={total}
          page={page}
          limit={limit}
          loading={loading}
          rowsPerPageOptions={[5, 10, 25, 50]}
          onTableChange={handleTableChange}
          extraButtons={
            <Button size="small" variant="outlined" onClick={handleDownloadCsv}>
              Download CSV
            </Button>
          }
        />
      </Box>

      <Dialog
        open={!!editId}
        onClose={resetForm}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, fontSize: 24, fontFamily: 'Aeonik', pb: 0 }}
        >
          Lucky Draw Optin
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>{editId ? optinForm : null}</DialogContent>
      </Dialog>

      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
            color: '#fff',
            fontWeight: 700,
            fontFamily: 'Aeonik',
            fontSize: 20,
            py: 2,
          }}
        >
          History{historyJeweller ? ` — ${historyJeweller}` : ''}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {historyRows.length ? (
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    {['#', 'Status', 'Updated By', 'Updated At'].map((label) => (
                      <TableCell
                        key={label}
                        sx={{
                          fontWeight: 700,
                          fontFamily: 'Aeonik',
                          backgroundColor: '#f6f1ff',
                          color: '#3b0764',
                          borderBottom: '1px solid #e9d5ff',
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRows.map((item, index) => {
                    const updatedBy = item.updateBy || item.updatedBy;
                    const updatedByName =
                      typeof updatedBy === 'string'
                        ? updatedBy
                        : updatedBy?.name || '-';
                    const optedIn = !!item.optInStatus;
                    return (
                      <TableRow
                        key={item._id || index}
                        sx={{
                          '&:nth-of-type(even)': { backgroundColor: '#fafafa' },
                          '&:last-child td': { borderBottom: 0 },
                        }}
                      >
                        <TableCell sx={{ color: '#6b7280', width: 64 }}>
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={optedIn ? 'Opt In' : 'Opt Out'}
                            sx={{
                              fontWeight: 700,
                              backgroundColor: optedIn ? '#e8f5e9' : '#fdecea',
                              color: optedIn ? '#1b5e20' : '#c62828',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {updatedByName}
                        </TableCell>
                        <TableCell>
                          {item.updatedAt
                            ? moment(item.updatedAt).format('DD-MM-YYYY hh:mm A')
                            : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary" fontFamily="Aeonik">
                No history available
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eee' }}>
          <Button
            variant="contained"
            onClick={() => setHistoryOpen(false)}
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)',
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openConfirm} onClose={handleCancel}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{' '}
            <b>
              {selectedRow?.optInStatus ? 'opt out' : 'opt in'}{' '}
              {selectedRow?.jewellerName}
            </b>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel}
            variant="contained"
            sx={{ backgroundColor: '#d32f2f', color: 'white' }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{ backgroundColor: 'green', color: 'white' }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LuckyDrawOptim;
